import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TransportManagementDefaultProfileService } from './transport-management-default-profile.service';
import {
  TransportManagementDefaultProfileAffectedUsersLoad,
  TransportManagementDefaultProfileInitialState,
  TransportManagementDefaultProfileLoad,
  TransportManagementDefaultProfileSetToggle,
} from './transport-management-default-profile.actions';
import { TransportManagementProfileModel } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportManagementDefaultProfileStateModel {
  defaultProfile: TransportManagementProfileModel;
  affectedUsers: number;
  loaded: boolean;
}

const initialState: TransportManagementDefaultProfileStateModel = {
  defaultProfile: null,
  affectedUsers: null,
  loaded: false,
};

@State<TransportManagementDefaultProfileStateModel>({
  name: 'transportManagementDefaultProfile',
  defaults: initialState,
})
@Injectable()
export class TransportManagementDefaultProfileState {
  constructor(
    private transportManagementDefaultProfileService: TransportManagementDefaultProfileService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getDefaultProfile(state: TransportManagementDefaultProfileStateModel): TransportManagementProfileModel {
    return state.defaultProfile;
  }

  @Selector()
  static getLoaded(state: TransportManagementDefaultProfileStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getAffectedUsers(state: TransportManagementDefaultProfileStateModel): number {
    return state.affectedUsers;
  }

  @Action(TransportManagementDefaultProfileSetToggle)
  setTransportManagementProfileItemDefaultProfileToggle(
    ctx: StateContext<TransportManagementDefaultProfileStateModel>,
    { payload }: TransportManagementDefaultProfileSetToggle,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementDefaultProfileService
      .setTransportManagementProfileItemDefaultProfileToggle(payload)
      .pipe(
        map(() => {
          this.notificationService.success('api.transportManagementProfile.success.update');
        }),
        catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(TransportManagementDefaultProfileLoad)
  getTransportManagementProfileItemDefaultProfile(
    ctx: StateContext<TransportManagementDefaultProfileStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementDefaultProfileService.getTransportManagementDefaultProfile().pipe(
      map((defaultProfile: TransportManagementProfileModel) => {
        ctx.patchState({ defaultProfile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementDefaultProfileAffectedUsersLoad)
  getTransportManagementProfileItemDefaultProfileAffectedUsers(
    ctx: StateContext<TransportManagementDefaultProfileStateModel>,
    { payload }: TransportManagementDefaultProfileAffectedUsersLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementDefaultProfileService
      .getTransportManagementDefaultProfileAffectedUsers(payload)
      .pipe(
        map((affectedUsers: number) => {
          ctx.patchState({ affectedUsers });
        }),
        catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(TransportManagementDefaultProfileInitialState)
  setInitialState(ctx: StateContext<TransportManagementDefaultProfileStateModel>): void {
    ctx.setState(initialState);
  }
}
